import { useEffect, useRef, useState } from 'react'
import { ChevronDownIcon } from './icons.jsx'

// Shared filter primitives: the dropdown chip every filtered table uses, the
// checkbox row inside it, and the tiny sparkline. These lived in SegmentKit
// alongside the saved-segments UI; segments folded into the leads page, so the
// pieces that outlived it live here on their own.

// Close the popover on any click outside it, and on Escape. Lived in
// SegmentKit; rebuilt here so these primitives have no dependency on the
// deleted saved-segments code.
function useClickOutside(ref, onOutside) {
  useEffect(() => {
    function onPointer(e) {
      if (ref.current && !ref.current.contains(e.target)) onOutside()
    }
    function onKey(e) {
      if (e.key === 'Escape') onOutside()
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [ref, onOutside])
}

// The chip button itself — matches the inline filter buttons next to it (see the
// "Has contact" toggle on the leads pages) so a chip and a toggle read as one
// control family.
function chipClass(active) {
  return `inline-flex cursor-pointer items-center gap-x-1.5 rounded-[10px] border px-3 py-2 text-[13px] font-semibold transition-colors ${
    active
      ? 'border-primary bg-primary-tint text-primary-dark'
      : 'border-line bg-surface text-dim hover:bg-panel hover:text-ink'
  }`
}

export function FilterChip({ label, count, active, onClear, children }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useClickOutside(ref, () => setOpen(false))
  return (
    <div className="relative" ref={ref}>
      <button type="button" className={chipClass(active)} onClick={() => setOpen((o) => !o)}>
        {label}
        {count > 0 && (
          <span className="rounded-full bg-primary-tint px-[7px] py-px text-[11px] font-semibold text-primary-dark">
            {count}
          </span>
        )}
        {active ? (
          <span
            role="button"
            aria-label={`Clear ${label}`}
            className="ml-0.5 cursor-pointer text-faint hover:text-ink"
            onClick={(e) => {
              e.stopPropagation()
              onClear()
              setOpen(false)
            }}
          >
            ✕
          </span>
        ) : (
          <ChevronDownIcon className="h-[14px] w-[14px] text-faint" />
        )}
      </button>
      {open && (
        <div className="absolute left-0 top-[calc(100%+6px)] z-40 min-w-[240px] rounded-[12px] border border-line bg-surface p-3 shadow-[0_12px_32px_rgba(15,23,42,0.16)]">
          {children}
        </div>
      )}
    </div>
  )
}

export function CheckRow({ checked, onToggle, children, sub }) {
  return (
    <label className="flex cursor-pointer items-center gap-x-2.5 rounded-[8px] px-2 py-[7px] hover:bg-panel">
      <input
        type="checkbox"
        className="h-[15px] w-[15px] flex-none cursor-pointer accent-primary"
        checked={checked}
        onChange={onToggle}
      />
      <span className="min-w-0 flex-1 truncate text-[13px] text-ink">{children}</span>
      {sub != null && <span className="text-[12px] tabular-nums text-faint">{sub}</span>}
    </label>
  )
}

// ---------- Sparkline ----------
export function Sparkline({ points, width = 120, height = 32 }) {
  if (!points || points.length < 2) {
    return <span className="text-[12px] text-faint">Opens build history</span>
  }
  const max = Math.max(...points, 1)
  const min = Math.min(...points, 0)
  const range = max - min || 1
  const step = width / (points.length - 1)
  const d = points
    .map((p, i) => {
      const x = (i * step).toFixed(1)
      const y = (height - 3 - ((p - min) / range) * (height - 6)).toFixed(1)
      return `${i === 0 ? 'M' : 'L'}${x},${y}`
    })
    .join(' ')
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <path d={d} fill="none" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}
